import { useChangePassword } from '@lopo-tech/lopo-api'
import React, { useCallback, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { Button } from 'src/components/button/button'
import InputField from 'src/components/forms/input_field/input_field'
import MessageSegmentWithModal from 'src/components/message_segment/message_segment_with_modal'
import { CLASS_PREFIX } from 'src/constants'
import { PASSWORD_RESET_SUCCESS_TIMEOUT } from 'src/constants/timeouts'
import { textResources } from 'src/lang/de'
import { validatePassword, validateRepeatedPassword } from 'src/utils/validation'
import { AUTH_CLS } from '../../auth/constants'
import './reset_password_form.scss'

const cls = `${CLASS_PREFIX}reset-password-form`

type IProps = {
  token: string
  email: string
  onGoToForgotPassword?: () => void
  onGoToLogin?: () => void
}

export const ResetPasswordForm: React.FC<IProps> = ({ token, email, onGoToForgotPassword, onGoToLogin }) => {
  const [password, setPassword] = useState('')
  const [repeatedPassword, setRepeatedPassword] = useState('')
  const [validPassword, setValidPassword] = useState(true)
  const [validRepeatedPassword, setValidRepeatedPassword] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const [changePassword] = useChangePassword()
  const history = useHistory()

  const handleSubmit = useCallback(() => {
    const passwordValid = validatePassword(password)
    const repeatedValid = validateRepeatedPassword(password, repeatedPassword)

    setValidPassword(passwordValid)
    setValidRepeatedPassword(repeatedValid)

    if (!passwordValid || !repeatedValid) {
      return
    }

    setIsLoading(true)
    changePassword({
      variables: {
        input: {
          email,
          password,
          token
        }
      }
    }).then((response) => {
      const error = response.error
      if (error) {
        setErrorMessage(error.message)
        setValidPassword(false)
        return
      }
      setShowSuccess(true)
      setTimeout(() => history.push('/'), PASSWORD_RESET_SUCCESS_TIMEOUT)
    }).catch(() => {
      setErrorMessage(textResources.shared.errorUnknown)
    }).finally(() => {
      setIsLoading(false)
    })
  }, [password, repeatedPassword, email, token, changePassword, history])

  if (showSuccess) {
    return (
      <MessageSegmentWithModal
        title={textResources.authentication.passwordChanged}
        description={textResources.authentication.passwordChangedLongText}
        image='password'
      />
    )
  }

  if (!token || !email) {
    return (
      <div className={cls}>
        <div className={`${cls}__error-state`}>
          <div className={`${AUTH_CLS}__header`}>{textResources.authentication.passwordResetTitle}</div>
          <p className={`${cls}__error`}>{textResources.shared.errorUnknown}</p>
          {onGoToLogin && <Button size='xlarge' title={textResources.shared.login} onClick={onGoToLogin} />}
          {onGoToForgotPassword && <Button size='xlarge' variant="secondary" title={textResources.authentication.passwordForgot} onClick={onGoToForgotPassword} />}
        </div>
      </div>
    )
  }

  return (
    <div className={cls}>
      <div className={`${cls}__content`}>
        <div className={`${AUTH_CLS}__header`}>{textResources.authentication.passwordResetTitle}</div>
        <InputField
          floatLabel={false}
          name="password"
          label={textResources.authentication.newPassword}
          placeholder={textResources.authentication.newPassword}
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(value) => { setPassword(value); setValidPassword(true) }}
          valid={validPassword}
          errorText={!validPassword ? textResources.authentication.errorPassword : undefined}
        />
        <InputField
          floatLabel={false}
          name="repeatedPassword"
          label={textResources.authentication.newPasswordRepeat}
          placeholder={textResources.authentication.newPasswordRepeat}
          type="password"
          autoComplete="new-password"
          value={repeatedPassword}
          onChange={(value) => { setRepeatedPassword(value); setValidRepeatedPassword(true) }}
          valid={validRepeatedPassword}
          errorText={!validRepeatedPassword ? textResources.authentication.errorRepeatPassword : undefined}
        />
        {errorMessage && <div className={`${cls}__error`}>{errorMessage}</div>}
        <Button
          size='xlarge'
          title={textResources.authentication.confirmPasswordReset}
          disabled={isLoading}
          onClick={handleSubmit}
        />
      </div>
    </div>
  )
}
